import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import InfoCard from "@/components/ui/info-card";
import { Employees, Book, User } from "@/components/icons";
import { PercentageOutlined } from "@ant-design/icons";
import { dashboardApi } from "../services/dashboard.api";
import type {
  DashboardSummary,
  DashboardEducationStatus,
  DashboardCategorySuccessCompletion,
} from "../types/dashboard.types";
import UpcomingTrainingsTable from "../components/UpcomingTrainingsTable";
import DashboardAnnouncementsList from "../components/DashboardAnnouncementsList";
import TodaysTasksWidget from "../components/TodaysTasksWidget";
import EducationStatusChart from "../components/EducationStatusChart";
import CategoryPerformanceChart from "../components/CategoryPerformanceChart";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./DashboardPage.module.scss";

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canSeeUpcomingTrainings = hasPermission(PERMISSIONS.EDUCATION_VIEW);
  const canSeeAnnouncements = hasPermission(PERMISSIONS.ANNOUNCEMENTS_VIEW);
  const canViewStaff = hasPermission(PERMISSIONS.STAFF_VIEW);
  const canViewEducation = hasPermission(PERMISSIONS.EDUCATION_VIEW);
  const canViewReports = hasPermission(PERMISSIONS.REPORTS_VIEW);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [educationStatus, setEducationStatus] =
    useState<DashboardEducationStatus | null>(null);
  const [categorySuccessCompletion, setCategorySuccessCompletion] =
    useState<DashboardCategorySuccessCompletion | null>(null);
  const [periodDays, setPeriodDays] = useState<7 | 30 | 90>(30);

  useEffect(() => {
    let cancelled = false;
    dashboardApi
      .getDashboardSummary({ periodDays })
      .then((data) => {
        if (cancelled) return;
        setSummary(data.summary);
        setEducationStatus(data.educationStatus);
        setCategorySuccessCompletion(data.categorySuccessCompletion);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error("Error loading dashboard summary:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [periodDays]);

  const infoCards = useMemo(
    () => [
      {
        key: "totalPersonnel" as const,
        value:
          summary?.totalPersonnelCount?.toLocaleString("tr-TR") ?? "-",
        icon: <Employees width={24} height={24} color="#fff" />,
        iconBgColor: "#3b82f6",
        target: canViewStaff ? "/dashboard/staff-management" : null,
      },
      {
        key: "totalTrainings" as const,
        value:
          summary?.totalEducationCount?.toLocaleString("tr-TR") ?? "-",
        icon: <Book width={24} height={24} color="#fff" />,
        iconBgColor: "#8b5cf6",
        target: canViewEducation ? "/dashboard/education-management" : null,
      },
      {
        key: "activeTrainees" as const,
        value:
          summary?.activeTraineeCount?.toLocaleString("tr-TR") ?? "-",
        icon: <User width={24} height={24} color="#fff" />,
        iconBgColor: "#22c55e",
        // status=2 (ACTIVE) ön-seçili filtreyle eğitim listesi
        target: canViewEducation
          ? "/dashboard/education-management?status=2"
          : null,
      },
      {
        key: "completionRate" as const,
        value:
          summary?.educationCompletionRatePercent != null
            ? `%${summary.educationCompletionRatePercent}`
            : "-",
        icon: <PercentageOutlined style={{ fontSize: 24, color: "#fff" }} />,
        iconBgColor: "#f59e0b",
        target: canViewReports ? "/dashboard/reports" : null,
      },
    ],
    [summary, canViewStaff, canViewEducation, canViewReports],
  );

  return (
    <DashboardLayout>
      <Header
        title={t("dashboard.title")}
        description={t("dashboard.description")}
      />

      <div className={styles.pageContainer}>
        <TodaysTasksWidget />
        <div className={styles.infoCardGrid}>
          {infoCards.map(({ key, value, icon, iconBgColor, target }) => (
            <InfoCard
              key={key}
              title={t(`dashboard.${key}`)}
              value={value}
              icon={icon}
              iconBgColor={iconBgColor}
              onClick={target ? () => navigate(target) : undefined}
              disabled={!target}
            />
          ))}
        </div>

        <div className={styles.periodBar}>
          <span className={styles.periodLabel}>
            {t("dashboard.period.label")}
          </span>
          <div
            role="tablist"
            aria-label={t("dashboard.period.label")}
            className={styles.periodSegmented}
          >
            {([7, 30, 90] as const).map((d) => (
              <button
                key={d}
                role="tab"
                type="button"
                aria-selected={periodDays === d}
                className={`${styles.periodButton} ${periodDays === d ? styles.periodButtonActive : ""
                  }`}
                onClick={() => setPeriodDays(d)}
              >
                {t(`dashboard.period.d${d}`, `Son ${d}g`)}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.chartsRow}>
          <EducationStatusChart
            title={t("dashboard.educationStatus")}
            data={educationStatus}
            totalLabel={t("dashboard.total")}
          />
          <CategoryPerformanceChart
            title={t("dashboard.categorySuccessCompletion")}
            data={categorySuccessCompletion}
            seriesLabels={{
              success: t("dashboard.seriesSuccess"),
              completion: t("dashboard.seriesCompletion"),
            }}
          />
        </div>

        {(canSeeUpcomingTrainings || canSeeAnnouncements) && (
          <div
            className={styles.tablesRow}
            data-single={
              canSeeUpcomingTrainings !== canSeeAnnouncements ? "true" : "false"
            }
          >
            {canSeeUpcomingTrainings && <UpcomingTrainingsTable />}
            {canSeeAnnouncements && (
              <DashboardAnnouncementsList viewAllPath="/dashboard/announcements" />
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
